import React from 'react';
import { formatCurrency } from '../../../lib/utils';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Separator } from '../../ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { FileText, Wifi, Droplets, Zap, Tv, WashingMachine, ScrollText, CreditCard, Shield } from 'lucide-react';

export const LeaseFormFields = ({ formData, setFormData, units, availableTenants, onUnitChange, isEdit = false, isRenew = false }) => {
  const calculateTotal = () => {
    const base = parseFloat(formData.base_rent) || 0;
    const internet = parseFloat(formData.internet) || 0;
    const water = parseFloat(formData.water) || 0;
    const electricity = parseFloat(formData.electricity) || 0;
    const appliances = parseFloat(formData.appliances) || 0;
    const laundry = parseFloat(formData.laundry) || 0;
    const other1 = parseFloat(formData.other1_amount) || 0;
    const other2 = parseFloat(formData.other2_amount) || 0;
    return base + internet + water + electricity + appliances + laundry + other1 + other2;
  };

  return (
    <div className="space-y-6 overflow-y-auto pr-2 flex-1">
      {/* Basic Info */}
      <div className="space-y-4">
        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Basic Information</h4>

        {!isEdit && !isRenew && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Select Unit *</Label>
              <Select value={formData.unit_id} onValueChange={onUnitChange}>
                <SelectTrigger data-testid="lease-unit-select">
                  <SelectValue placeholder="Select an available unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.unit_id} value={unit.unit_id}>
                      {unit.property_name} - Room {unit.unit_number} ({formatCurrency(unit.monthly_rent)}) [{unit.current_occupants || 0}/{unit.capacity} occupied]
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Select Tenant *</Label>
              <Select value={formData.tenant_id} onValueChange={(v) => setFormData({ ...formData, tenant_id: v })}>
                <SelectTrigger data-testid="lease-tenant-select">
                  <SelectValue placeholder="Select a tenant" />
                </SelectTrigger>
                <SelectContent>
                  {availableTenants.map((tenant) => (
                    <SelectItem key={tenant.user_id} value={tenant.user_id}>
                      {tenant.name} ({tenant.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start_date">Start Date *</Label>
            <Input
              id="start_date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end_date">End Date *</Label>
            <Input
              id="end_date"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="base_rent">Base Room Rent (₱) *</Label>
            <Input
              id="base_rent"
              type="text"
              value={formData.base_rent}
              onChange={(e) => setFormData(prev => ({ ...prev, base_rent: e.target.value }))}
              placeholder="0"
              data-testid="lease-base-rent-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="deposit_amount">Deposit (₱)</Label>
            <Input
              id="deposit_amount"
              type="text"
              value={formData.deposit_amount}
              onChange={(e) => setFormData(prev => ({ ...prev, deposit_amount: e.target.value }))}
              placeholder="0"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Additional Monthly Fees */}
      <div className="space-y-4">
        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Additional Monthly Fees</h4>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="internet" className="flex items-center gap-2">
              <Wifi className="w-4 h-4 text-blue-500" />
              Internet (₱)
            </Label>
            <Input
              id="internet"
              type="text"
              value={formData.internet}
              onChange={(e) => setFormData(prev => ({ ...prev, internet: e.target.value }))}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="water" className="flex items-center gap-2">
              <Droplets className="w-4 h-4 text-cyan-500" />
              Water (₱)
            </Label>
            <Input
              id="water"
              type="text"
              value={formData.water}
              onChange={(e) => setFormData(prev => ({ ...prev, water: e.target.value }))}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="electricity" className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              Electricity (₱)
            </Label>
            <Input
              id="electricity"
              type="text"
              value={formData.electricity}
              onChange={(e) => setFormData(prev => ({ ...prev, electricity: e.target.value }))}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="appliances" className="flex items-center gap-2">
              <Tv className="w-4 h-4 text-purple-500" />
              Appliances (₱)
            </Label>
            <Input
              id="appliances"
              type="text"
              value={formData.appliances}
              onChange={(e) => setFormData(prev => ({ ...prev, appliances: e.target.value }))}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="laundry" className="flex items-center gap-2">
              <WashingMachine className="w-4 h-4 text-emerald-500" />
              Laundry (₱)
            </Label>
            <Input
              id="laundry"
              type="text"
              value={formData.laundry}
              onChange={(e) => setFormData(prev => ({ ...prev, laundry: e.target.value }))}
              placeholder="0"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground">Other Fee 1</Label>
            <div className="flex gap-2">
              <Input
                value={formData.other1_name}
                onChange={(e) => setFormData(prev => ({ ...prev, other1_name: e.target.value }))}
                placeholder="Fee name"
                className="flex-1"
              />
              <Input
                type="text"
                value={formData.other1_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, other1_amount: e.target.value }))}
                placeholder="₱ Amount"
                className="w-28"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground">Other Fee 2</Label>
            <div className="flex gap-2">
              <Input
                value={formData.other2_name}
                onChange={(e) => setFormData(prev => ({ ...prev, other2_name: e.target.value }))}
                placeholder="Fee name"
                className="flex-1"
              />
              <Input
                type="text"
                value={formData.other2_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, other2_amount: e.target.value }))}
                placeholder="₱ Amount"
                className="w-28"
              />
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Lease Terms */}
      <div className="space-y-4">
        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <ScrollText className="w-4 h-4" />
          Lease Agreement Terms
        </h4>

        <Accordion type="multiple" className="w-full">
          <AccordionItem value="house_rules">
            <AccordionTrigger className="text-sm">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-500" />
                House Rules and Regulations
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Textarea
                value={formData.house_rules}
                onChange={(e) => setFormData(prev => ({ ...prev, house_rules: e.target.value }))}
                placeholder="Enter house rules and regulations...&#10;&#10;Example:&#10;1. Quiet hours: 10:00 PM - 7:00 AM&#10;2. No smoking inside the premises&#10;3. Visitors must be registered&#10;4. Keep common areas clean"
                className="min-h-[150px]"
                data-testid="lease-house-rules-input"
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="payment_terms">
            <AccordionTrigger className="text-sm">
              <span className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-emerald-500" />
                Payment Terms
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Textarea
                value={formData.payment_terms}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                placeholder="Enter payment terms...&#10;&#10;Example:&#10;1. Rent is due on or before the 5th of each month&#10;2. Late payment fee: ₱500 after the 10th&#10;3. Accepted payment methods: GCash, Maya, Bank Transfer&#10;4. Security deposit is refundable upon move-out inspection"
                className="min-h-[150px]"
                data-testid="lease-payment-terms-input"
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="terms_of_service">
            <AccordionTrigger className="text-sm">
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-500" />
                Terms of Service
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Textarea
                value={formData.terms_of_service}
                onChange={(e) => setFormData(prev => ({ ...prev, terms_of_service: e.target.value }))}
                placeholder="Enter terms of service...&#10;&#10;Example:&#10;1. Lease may be terminated with 30 days written notice&#10;2. Tenant is responsible for minor repairs under ₱500&#10;3. Major repairs and maintenance are landlord's responsibility&#10;4. No unauthorized alterations to the unit"
                className="min-h-[150px]"
                data-testid="lease-tos-input"
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <Separator />

      {/* Total */}
      <div className="p-4 bg-[#C85A42]/10 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="font-semibold">Total Monthly Rent</span>
          <span className="text-2xl font-bold text-[#C85A42]" data-testid="lease-total-rent">
            {formatCurrency(calculateTotal())}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Base rent + all additional fees
        </p>
      </div>
    </div>
  );
};
